import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTablePFSEvent } from "primereact/datatable";
import { useCallback, useState } from "react";
import { DCSQueryClient } from "../../../queryClient";
import { If } from "../../../components/If/If";
import { UserRoles } from "../../../constants/userRoles";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { useToast } from "../../../Providers/ToastProvider";
import { PrivateAccess } from "../../../keycloak";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";
import { DeleteJobZonePopup } from "./components/DeleteJobZonePopup/DeleteJobZonePopup";
import { JobZoneList } from "./components/JobZoneList/JobZoneList";
import { SetJobZone } from "./components/SetJobZone/SetJobZone";
import { IJobZone } from "./JobZone.model";

import classes from "./JobZone.module.scss";
import { useEffectWithoutMount } from "../../../helpers/helperHooks";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const JobZone = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<IJobZone | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);

  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["job-zones"],
    queryFn: () => {
      return requests.get(`/job-zone?${filterQuery}`);
    },
  });

  const addItemRequest = useMutation({
    mutationFn: (newJobZone: IJobZone) => {
      return requests.post("/job-zone", {
        name: newJobZone.name,
        employeeId: newJobZone.employeeId,
        supervisorId: newJobZone.supervisorId,
      });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["job-zones"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({ zoneCode, name, employeeId, supervisorId }: IJobZone) => {
      return requests.put(`/job-zone/${zoneCode}`, {
        name,
        employeeId,
        supervisorId,
      });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["job-zones"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({ zoneCode }: IJobZone) => {
      return requests.delete(`/job-zone/${zoneCode}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["job-zones"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleAddJobZone = (jobZone: IJobZone) => {
    addItemRequest.mutate(jobZone);
  };

  const handleUpdateJobZone = (jobZone: IJobZone) => {
    editItemRequest.mutate(jobZone);
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  const editItem = (jobZone: any) => {
    setItem({
      zoneCode: jobZone.zoneCode,
      name: jobZone.name,
      supervisorId: jobZone.supervisor.map((supervisor: any) => supervisor.id),
      employeeId: jobZone.employees.map((employee: any) => employee.id),
    });
    setItemDialog(true);
  };

  const confirmDeleteItem = (jobZone: IJobZone) => {
    setItem(jobZone);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  const handleAddItem = () => {
    setItem({
      zoneCode: null,
      name: "",
      employeeId: [],
      supervisorId: [],
    });
    setItemDialog(true);
  };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Service Division</h4>
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.JOB_ZONE_CREATE],
          }}
        >
          <Button
            icon="pi pi-plus"
            className=""
            label="New"
            onClick={handleAddItem}
          />
        </PrivateAccess>
      </div>
    );
  };

  return (
    <div>
      <div className={classes.container}>
        <Card title={cardTitle}>
          <JobZoneList
            totalRecords={data?.total}
            records={data?.result}
            first={filters.first}
            rows={filters.rows}
            onPage={onPage}
            rowActionDelete={confirmDeleteItem}
            rowActionEdit={editItem}
          />
        </Card>
        <If condition={deleteItemDialog}>
          <DeleteJobZonePopup
            item={item}
            onHide={hideDeleteItemDialog}
            onConfirm={deleteItem.mutate}
            deleteRequestPending={deleteItem.isLoading}
          />
        </If>
        <If condition={!!itemDialog}>
          <SetJobZone
            item={item}
            onHide={hideItemDialog}
            onAdd={handleAddJobZone}
            onUpdate={handleUpdateJobZone}
            requestPending={
              addItemRequest.isLoading || editItemRequest.isLoading
            }
          />
        </If>
      </div>
    </div>
  );
};
